import { useCallback, useEffect, useRef, useState } from 'react';
import { Modal, Button, Badge } from '../../../shared/components';
import { getTurnoGeoRepository } from '../../../shared/services/turnoGeo/repositoryFactory';
import { PendingPing, RespondPingResult } from '../../../shared/services/turnoGeo/types';

const repo = getTurnoGeoRepository();

type Gps = { latitude: number | null; longitude: number | null; accuracy: number | null; permissionDenied: boolean };

function readGps(): Promise<Gps> {
  return new Promise((resolve) => {
    if (!('geolocation' in navigator)) return resolve({ latitude: null, longitude: null, accuracy: null, permissionDenied: false });
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ latitude: p.coords.latitude, longitude: p.coords.longitude, accuracy: p.coords.accuracy, permissionDenied: false }),
      (err) => resolve({ latitude: null, longitude: null, accuracy: null, permissionDenied: err.code === err.PERMISSION_DENIED }),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  });
}

const mmss = (ms: number) => {
  const s = Math.max(0, Math.round(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

/**
 * Vigilante de toques: mientras la aplicación está abierta (en cualquier pantalla de TurnoGeo)
 * consulta cada minuto si el servidor sorteó un toque de ubicación para ahora y, si lo hay,
 * pide confirmar dónde está. El servidor clasifica con las mismas reglas del marcaje; lo que
 * no cuadre queda en Inconsistencias por auditar. Los toques futuros nunca se informan.
 */
export function PingWatcher() {
  const [ping, setPing] = useState<PendingPing | null>(null);
  const [now, setNow] = useState(Date.now());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<RespondPingResult | null>(null);
  const resultOpen = useRef(false);

  const check = useCallback(() => {
    if (resultOpen.current) return;
    repo
      .getMyDay()
      .then((d) => setPing(d.pendingPing))
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    check();
    const id = setInterval(() => document.visibilityState === 'visible' && check(), 60_000);
    const onVisible = () => document.visibilityState === 'visible' && check();
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [check]);

  useEffect(() => {
    if (!ping) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [ping]);

  const expired = ping ? now > new Date(ping.expiresAt).getTime() : false;

  async function confirm() {
    if (!ping) return;
    setBusy(true);
    setError(null);
    try {
      const gps = await readGps();
      const r = await repo.respondPing(ping.id, {
        detectedLatitude: gps.latitude,
        detectedLongitude: gps.longitude,
        gpsAccuracyMeters: gps.accuracy,
        locationPermissionDenied: gps.permissionDenied,
      });
      resultOpen.current = true;
      setResult(r);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo confirmar tu ubicación.');
    } finally {
      setBusy(false);
    }
  }

  function close() {
    resultOpen.current = false;
    setResult(null);
    setPing(null);
    setError(null);
    check();
  }

  if (!ping && !result) return null;
  return (
    <Modal open onClose={() => (result || expired ? close() : undefined)} title="Confirma tu ubicación">
      {result ? (
        <div className="space-y-3">
          <div className={['rounded-xl p-4 text-sm', result.inRange ? 'bg-brand-light text-ink-800' : 'bg-amber-50 text-ink-800'].join(' ')}>
            <p className="mb-1 flex items-center gap-2 font-semibold">
              {result.inRange ? '✅ Ubicación confirmada' : '⚠️ No pudimos validar tu ubicación'}
              <Badge tone={result.inRange ? 'success' : 'warning'}>{result.inRange ? 'En rango' : 'Fuera de rango'}</Badge>
            </p>
            <p className="text-xs text-ink-600">{result.reason}</p>
          </div>
          {!result.inRange && <p className="text-xs text-ink-500">Quedó registrado para revisión del administrador. Si estás en tu lugar de trabajo, verifica que el GPS esté activo y que estés en la red correcta.</p>}
          <Button fullWidth onClick={close}>Entendido</Button>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-ink-700">
            Es un chequeo de rutina de tu jornada: toca el botón para confirmar dónde estás. Tienes{' '}
            <strong>{expired ? '0:00' : mmss(new Date(ping!.expiresAt).getTime() - now)}</strong> para responder.
          </p>
          {expired && <p className="rounded-xl bg-amber-50 p-3 text-xs text-ink-700">Este toque venció y quedó registrado como sin respuesta. Puedes seguir trabajando con normalidad.</p>}
          {error && <p className="rounded-xl bg-red-50 p-3 text-xs text-critical">{error}</p>}
          {expired ? <Button fullWidth variant="outline" onClick={close}>Cerrar</Button> : <Button fullWidth onClick={confirm} isLoading={busy}>Confirmar mi ubicación</Button>}
        </div>
      )}
    </Modal>
  );
}
